import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Star, Trash2 } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'

type Role =
  | 'manager'
  | 'tour_manager'
  | 'production_manager'
  | 'marketing'
  | 'press'
  | 'legal'
  | 'accounting'
  | 'booking_agent'
  | 'personal_assistant'
  | 'security'
  | 'wardrobe'
  | 'medic'
  | 'social_media'
  | 'other'

interface TeamMember {
  id: string
  name: string
  email: string
  phone: string | null
  role: Role
  isPrimary: boolean
  notes: string | null
  active: boolean
}

const ROLE_OPTIONS: Role[] = [
  'manager',
  'tour_manager',
  'production_manager',
  'marketing',
  'press',
  'legal',
  'accounting',
  'booking_agent',
  'personal_assistant',
  'security',
  'wardrobe',
  'medic',
  'social_media',
  'other',
]

export function TeamPage() {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)

  const { data, error, isLoading } = useQuery<TeamMember[], ApiClientError>({
    queryKey: ['team'],
    queryFn: () => artistFetch<TeamMember[]>('/team'),
  })

  async function removeMember(id: string) {
    if (!confirm('Remove this team member?')) return
    try {
      await artistFetch(`/team/${id}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['team'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    }
  }

  return (
    <div>
      <PageHeader
        title="Team"
        subtitle="The people who keep the artist running. Used by the email router to decide who handles each incoming message."
        actions={
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            {adding ? 'Cancel' : 'Add member'}
          </button>
        }
      />

      {adding && (
        <AddMemberForm
          onAdded={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['team'] })
          }}
        />
      )}

      <AsyncBoundary isLoading={isLoading} error={error}>
        <DataTable
          rows={data ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No team members yet. Add the key roles (tour manager, marketing, press, legal) so the email router knows where to send things."
          columns={[
            {
              key: 'name',
              header: 'Name',
              render: (r) => (
                <div className="flex items-center gap-2">
                  {r.isPrimary && (
                    <Star className="h-3 w-3 fill-amber-400 text-amber-500" />
                  )}
                  <span>{r.name}</span>
                </div>
              ),
            },
            { key: 'email', header: 'Email', render: (r) => r.email },
            {
              key: 'role',
              header: 'Role',
              render: (r) => (
                <span className="capitalize">{r.role.replace(/_/g, ' ')}</span>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (r) =>
                r.active ? (
                  <span className="text-emerald-700">Active</span>
                ) : (
                  <span className="text-ovation-muted">Inactive</span>
                ),
            },
            {
              key: 'actions',
              header: '',
              align: 'right',
              render: (r) => (
                <button
                  type="button"
                  onClick={() => removeMember(r.id)}
                  className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-3 w-3" />
                  Remove
                </button>
              ),
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}

function AddMemberForm({ onAdded }: { onAdded: () => void }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('tour_manager')
  const [isPrimary, setIsPrimary] = useState(true)
  const [notes, setNotes] = useState('')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      await artistFetch('/team', {
        method: 'POST',
        body: {
          name,
          email,
          role,
          isPrimary,
          ...(notes ? { notes } : {}),
        },
      })
      onAdded()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium">Name</label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 pt-6">
          <input
            id="isPrimary"
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
          />
          <label htmlFor="isPrimary" className="text-sm">
            Primary person for this role
          </label>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          placeholder="Optional context the email router can surface to triagers"
        />
      </div>
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
      >
        {submitting ? 'Adding…' : 'Add member'}
      </button>
    </form>
  )
}
